import React, { useEffect, useState } from 'react';
import { CheckCircle, ShieldAlert, ClipboardList, Lock } from 'lucide-react';
import FormControl from '../FormControl';
import Modal from '../Modal';
import Button from '../Button';
import Toast, { type ToastMessage } from '../Toast';
import { useFieldValidation } from '../../../validation/useFieldValidation';
import { maxLenForCode } from '../../../validation/engine';
import { TriageWorkbenchProps } from './types';

const TRIVIAL_REASON_OPTIONS = [
    { value: 'False Positive', label: 'False Positive' },
    { value: 'Duplicate Incident', label: 'Duplicate Incident' },
    { value: 'Policy Misunderstanding', label: 'Policy Misunderstanding' },
    { value: 'Test / Drill', label: 'Test / Drill' },
];

type TriageMode = 'initial' | 'trivial' | 'escalate' | 'task';

const TRIAGE_TABS: {
    key: Exclude<TriageMode, 'initial'>;
    title: string;
    desc: string;
    icon: typeof CheckCircle;
    testid: string;
    activeBorder: string;
    activeBg: string;
    activeRing: string;
    iconActiveBg: string;
    iconBg: string;
    iconText: string;
}[] = [
    {
        key: 'trivial', title: 'Mark Trivial', desc: 'Close with no further action.',
        icon: CheckCircle, testid: 'BTN_incident_triage_mode_trivial',
        activeBorder: 'border-green-500', activeBg: 'bg-green-50', activeRing: 'ring-green-500/30',
        iconActiveBg: 'bg-green-500', iconBg: 'bg-green-50', iconText: 'text-green-600',
    },
    {
        key: 'escalate', title: 'Escalate', desc: 'Raise CAPA issue & assign owner.',
        icon: ShieldAlert, testid: 'BTN_incident_triage_mode_escalate',
        activeBorder: 'border-red-500', activeBg: 'bg-red-50', activeRing: 'ring-red-500/30',
        iconActiveBg: 'bg-red-500', iconBg: 'bg-red-50', iconText: 'text-red-600',
    },
    {
        key: 'task', title: 'Assign Task', desc: 'Create ad-hoc task for team.',
        icon: ClipboardList, testid: 'BTN_incident_triage_mode_task',
        activeBorder: 'border-blue-500', activeBg: 'bg-blue-50', activeRing: 'ring-blue-500/30',
        iconActiveBg: 'bg-blue-500', iconBg: 'bg-blue-50', iconText: 'text-blue-600',
    },
];

const SEVERITY_OPTIONS = [
    { value: 'Critical', label: 'Critical' },
    { value: 'High', label: 'High' },
    { value: 'Medium', label: 'Medium' },
    { value: 'Low', label: 'Low' },
];

// Statuses where the spawned task has reached a state the admin may
// reasonably consider 'done' — i.e. it is safe to manually close the
// originating record from the triage popup.
const TASK_CLOSEABLE_STATUSES = new Set(['DONE', 'VERIFIED', 'ARCHIVED']);

const TriageWorkbench: React.FC<TriageWorkbenchProps> = ({ isOpen, subject, ownerOptions, onClose, onUpdated }) => {
    // Preserve the last valid subject so modal body stays populated during the close animation.
    const lastSubjectRef = React.useRef<typeof subject>(subject);
    if (subject) lastSubjectRef.current = subject;
    const activeSubject = lastSubjectRef.current;

    const canTriage = !!activeSubject?.canTriage;
    // Details render in their original order. The Status field is shown inline
    // as a regular meta field alongside the others (no separate header badge).
    const allDetails = activeSubject?.details ?? [];
    // Short fields group into one inline meta row; long free-text (e.g.
    // Description) renders full-width below so nothing wastes a line.
    const metaShort = allDetails.filter(d => String(d.value ?? '').length <= 80);
    const longFields = allDetails.filter(d => String(d.value ?? '').length > 80);
    const linked = activeSubject?.linked;
    const linkedTask = linked?.task ?? null;
    const linkedTaskLoading = !!linked?.taskLoading;
    const showLinkedTask = !!linked && 'task' in linked;
    const linkedIssue = linked?.capa ?? null;
    const linkedIssueLoading = !!linked?.capaLoading;
    const showLinkedCapa = !!linked && 'capa' in linked;

    const [mode, setMode] = useState<TriageMode>('trivial');
    const activeTab = TRIAGE_TABS.find(t => t.key === mode) ?? null;
    const [trivialReason, setTrivialReason] = useState('False Positive');
    const [feedback, setFeedback] = useState('');
    const [riskSeverity, setRiskSeverity] = useState('Medium');
    const [capaOwnerId, setCapaOwnerId] = useState<number | null>(null);
    const [instructions, setInstructions] = useState('');
    const [targetDate, setTargetDate] = useState('');
    const [taskTitle, setTaskTitle] = useState('');
    const [taskAssigneeId, setTaskAssigneeId] = useState<number | null>(null);
    const [taskDueDate, setTaskDueDate] = useState('');
    const [taskDescription, setTaskDescription] = useState('');
    const [taskGraceDays, setTaskGraceDays] = useState<number>(0);
    const [taskApproverId, setTaskApproverId] = useState<number | null>(null);
    const [taskValidatorId, setTaskValidatorId] = useState<number | null>(null);
    const [taskPriority, setTaskPriority] = useState<string>('');
    const [submitting, setSubmitting] = useState(false);
    const [toast, setToast] = useState<ToastMessage | null>(null);
    const { errors, validateField, clearError, validateAll, reset } = useFieldValidation();

    // Clear stale inline errors when the modal (re)opens or the triage path changes.
    useEffect(() => { reset(); }, [isOpen, mode, reset]);

    // Reset ALL form field state when the modal opens for a different subject
    // (keyed on the record code) or is (re)opened. The component is never
    // unmounted between incidents — only `isOpen`/`subject` change — so without
    // this the previous incident's typed values bleed into the next triage.
    const subjectKey = subject?.code ?? null;
    useEffect(() => {
        if (!isOpen) return;
        setMode('trivial');
        setTrivialReason('False Positive');
        setFeedback('');
        setRiskSeverity('Medium');
        setCapaOwnerId(null);
        setInstructions('');
        setTargetDate('');
        setTaskTitle('');
        setTaskAssigneeId(null);
        setTaskDueDate('');
        setTaskDescription('');
        setTaskGraceDays(0);
        setTaskApproverId(null);
        setTaskValidatorId(null);
        setTaskPriority('');
        setToast(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, subjectKey]);

    const handleCloseRecord = async () => {
        if (!activeSubject?.onCloseRecord) return;
        setToast(null); setSubmitting(true);
        try {
            await activeSubject.onCloseRecord();
        } catch (e: any) {
            setToast({ type: 'error', message: e?.response?.data?.detail || e?.message || 'Failed to close' });
        } finally { setSubmitting(false); }
    };

    const handleSubmitTrivial = async () => {
        if (!validateAll([
            { name: 'feedback', code: 110, value: feedback, required: true },
        ])) return;
        if (!activeSubject) return;
        setToast(null); setSubmitting(true);
        try {
            await activeSubject.onTrivial({ reason: trivialReason, feedback });
            onUpdated();
        } catch (e: any) {
            setToast({ type: 'error', message: e?.response?.data?.detail || 'Failed to triage' });
        } finally { setSubmitting(false); }
    };

    const handleSubmitEscalation = async () => {
        if (!validateAll([
            { name: 'instructions', code: 110, value: instructions },
            { name: 'targetDate', code: 305, value: targetDate },
        ])) return;
        if (capaOwnerId == null) { setToast({ type: 'error', message: 'Please select a CAPA owner' }); return; }
        if (!activeSubject) return;
        setToast(null); setSubmitting(true);
        try {
            await activeSubject.onEscalate({
                risk_severity: riskSeverity,
                capa_owner_id: capaOwnerId,
                target_date: targetDate || undefined,
                instructions,
            });
            onUpdated();
        } catch (e: any) {
            setToast({ type: 'error', message: e?.response?.data?.detail || 'Failed to escalate' });
        } finally { setSubmitting(false); }
    };

    const handleSubmitTask = async () => {
        if (!validateAll([
            { name: 'taskTitle', code: 109, value: taskTitle, required: true },
            { name: 'taskDescription', code: 110, value: taskDescription },
            { name: 'taskDueDate', code: 305, value: taskDueDate },
            { name: 'taskGraceDays', code: 206, value: String(taskGraceDays) },
        ])) return;
        if (!taskTitle.trim()) { setToast({ type: 'error', message: 'Task title is required' }); return; }
        if (taskAssigneeId == null) { setToast({ type: 'error', message: 'Assignee is required' }); return; }
        if (taskGraceDays < 0) { setToast({ type: 'error', message: 'Grace period must be 0 or more days' }); return; }
        if (taskApproverId != null && taskValidatorId != null
            && taskAssigneeId === taskApproverId && taskApproverId === taskValidatorId) {
            setToast({ type: 'error', message: 'Doer, Approver and Validator cannot all be the same person' });
            return;
        }
        if (!activeSubject) return;
        setToast(null); setSubmitting(true);
        try {
            await activeSubject.onTask({
                task_title: taskTitle,
                task_description: taskDescription.trim() || undefined,
                task_assignee_id: taskAssigneeId,
                task_approver_id: taskApproverId,
                task_validator_id: taskValidatorId,
                task_due_date: taskDueDate || undefined,
                task_grace_period_days: taskGraceDays,
                task_priority: taskPriority || undefined,
            });
            onUpdated();
        } catch (e: any) {
            setToast({ type: 'error', message: e?.response?.data?.detail || 'Failed to create task' });
        } finally { setSubmitting(false); }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="xl" closeOnOverlayClick={false} contentClassName=" ">
            <Modal.Header>
                {activeSubject?.title}
            </Modal.Header>
            <Modal.Body className="!p-0">
                <div className="flex flex-col">
                    {/* Top summary strip — incident details */}
                    <div className="bg-gray-50/70 border-b border-gray-200 px-6 py-3">
                        <div className="flex items-center justify-between mb-2.5">
                            <h6 className="uppercase text-[11px] font-bold text-gray-400 tracking-wider">{activeSubject?.panelHeading ?? 'Record Details'}</h6>
                        </div>
                        {/* Short meta fields grouped in one inline row (Status lives in
                            the header badge); long fields (Description) get their own
                            full-width block below so nothing wastes a line. */}
                        {longFields.map((d, i) => (
                            <p key={i} className="text-[13px] text-gray-600 leading-relaxed mb-2.5">
                                <span className="text-[11px] uppercase tracking-wider font-semibold text-gray-500">{d.label} : </span>
                                {d.value || '—'}
                            </p>
                        ))}
                        {metaShort.length > 0 && (
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
                                {metaShort.map((d, i) => (
                                    <React.Fragment key={i}>
                                        {i > 0 && <span className="h-4 w-px bg-gray-300" aria-hidden="true" />}
                                        <div className="flex items-center gap-2 min-w-0">
                                            <span className="text-[11px] uppercase tracking-wider font-semibold text-gray-500">{d.label} :</span>
                                            <span className="text-sm text-gray-900 font-semibold truncate" title={String(d.value)}>{d.value || '—'}</span>
                                        </div>
                                    </React.Fragment>
                                ))}
                            </div>
                        )}
                        {/* linked task / CAPA chips */}
                        {(showLinkedTask || showLinkedCapa) && (
                            <div className="flex flex-wrap gap-2 mt-2 pt-2 border-t border-gray-200">
                                {showLinkedTask && (
                                    <>
                                        {linkedTaskLoading && <span className="text-xs text-gray-400 italic">Loading linked task…</span>}
                                        {!linkedTaskLoading && !linkedTask && <span className="text-xs text-gray-400 italic">No linked task.</span>}
                                        {linkedTask && (
                                            <span className="inline-flex items-center gap-1.5 text-xs bg-white border border-gray-200 rounded-full pl-2 pr-3 py-1">
                                                <ClipboardList size={12} className="text-blue-500" />
                                                <span className="font-medium text-gray-700">{linkedTask.name}</span>
                                                <span className="status-badge xs neutral">{linkedTask.status}</span>
                                                {linkedTask.doer_name && <span className="text-gray-500">→ {linkedTask.doer_name}</span>}
                                            </span>
                                        )}
                                    </>
                                )}
                                {showLinkedCapa && (
                                    <>
                                        {linkedIssueLoading && <span className="text-xs text-gray-400 italic">Loading linked CAPA…</span>}
                                        {!linkedIssueLoading && !linkedIssue && <span className="text-xs text-gray-400 italic">No linked CAPA issue.</span>}
                                        {linkedIssue && (
                                            <span className="inline-flex items-center gap-1.5 text-xs bg-white border border-gray-200 rounded-full pl-2 pr-3 py-1">
                                                <ShieldAlert size={12} className="text-red-500" />
                                                <span className="text-gray-500">{linkedIssue.issue_code}</span>
                                                <span className="font-medium text-gray-700">{linkedIssue.issue_name}</span>
                                                <span className={`status-badge xs ${linkedIssue.is_resolved ? 'success' : 'warning'}`}>
                                                    {linkedIssue.is_resolved ? 'Resolved' : 'Open'}
                                                </span>
                                                {typeof linkedIssue.open_action_count === 'number' && (
                                                    <span className="text-gray-500">{linkedIssue.open_action_count} open / {linkedIssue.action_count ?? 0}</span>
                                                )}
                                            </span>
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Workbench — triage paths + form */}
                    <div className="px-6 py-4">
                        <h6 className="uppercase text-[11px] font-bold text-gray-400 mb-4 tracking-wider pb-2.5 border-b border-gray-100">Triage Decision Workbench</h6>
                        {!canTriage && (
                            <div className="mb-4 p-3 rounded-md bg-amber-50 border border-amber-200 text-amber-700 text-sm flex items-center gap-2">
                                <Lock size={14} /> {activeSubject?.permissionHint || 'You do not have permission to triage this record. Contact your administrator.'}
                            </div>
                        )}
                        {canTriage && (
                        <div className="flex gap-5 items-stretch">
                            {/* Left vertical tab rail */}
                            <div className="w-64 flex-shrink-0 flex flex-col gap-2">
                                {TRIAGE_TABS.map(tab => {
                                    const Icon = tab.icon;
                                    const selected = mode === tab.key;
                                    return (
                                        <button
                                            key={tab.key}
                                            type="button"
                                            onClick={() => setMode(tab.key)}
                                            className={`group relative flex items-center gap-3 pl-4 pr-3 py-3 text-left rounded-lg border transition-all duration-200 overflow-hidden ${selected ? `${tab.activeBorder} ${tab.activeBg} shadow-sm` : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'}`}
                                            data-testid={tab.testid}
                                        >
                                            <span className={`inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg transition-colors ${selected ? `${tab.iconActiveBg} text-white` : `${tab.iconBg} ${tab.iconText}`}`}>
                                                <Icon size={18} />
                                            </span>
                                            <div className="min-w-0">
                                                <div className={`font-semibold text-sm ${selected ? 'text-gray-900' : 'text-gray-700'}`}>{tab.title}</div>
                                                <p className="text-xs text-gray-500 mt-0.5 leading-snug">{tab.desc}</p>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Right form panel */}
                            <div className="flex-1 min-w-0 rounded-xl border border-gray-200 bg-white overflow-hidden min-h-[260px] flex flex-col">
                        {/* form panel header — reflects the selected path */}
                        {activeTab && (
                            <div className="flex items-center gap-2.5 px-5 py-2 border-b border-gray-100 bg-gray-50/60">
                                <span className={`inline-flex h-7 w-7 items-center justify-center rounded-md ${activeTab.iconBg} ${activeTab.iconText}`}>
                                    <activeTab.icon size={15} />
                                </span>
                                <span className="font-semibold text-sm text-gray-800">{activeTab.title}</span>
                            </div>
                        )}
                        <div className="p-5 flex-1">
                        {mode === 'initial' && (
                            <div className="flex flex-col items-center justify-center text-center text-gray-400 py-16 h-full">
                                <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-gray-50 border border-gray-200 mb-4">
                                    <ClipboardList size={26} className="text-gray-300" />
                                </span>
                                <p className="text-sm font-medium text-gray-500">Select a triage path to begin</p>
                                <p className="text-xs text-gray-400 mt-1">Choose one of the actions on the left to proceed.</p>
                            </div>
                        )}

                        {mode === 'trivial' && (
                            <div className="space-y-3">
                                <div className="bg-green-50 border border-green-100 text-green-800 px-4 py-2 rounded-lg text-sm flex items-center">
                                    <CheckCircle size={16} className="mr-2 flex-shrink-0" /> This will close the ticket and automatically notify the user.
                                </div>
                                <FormControl.SearchSelect
                                    label="Reason for Rejection"
                                    options={TRIVIAL_REASON_OPTIONS}
                                    value={trivialReason}
                                    onChange={v => setTrivialReason(String(v ?? 'False Positive'))}
                                    data-testid="SEL_incident_triage_trivial_reason"
                                />
                                <FormControl.Textarea
                                    label="Feedback to Reporter (Required)"
                                    value={feedback}
                                    onChange={e => { setFeedback(e.target.value); clearError('feedback'); }}
                                    onBlur={() => validateField('feedback', 110, feedback, { required: true })}
                                    maxLength={maxLenForCode(110)}
                                    error={errors.feedback}
                                    data-testid="INP_incident_triage_trivial_feedback"
                                />
                            </div>
                        )}

                        {mode === 'escalate' && (
                            <div className="space-y-3">
                                <div className="bg-red-50 border border-red-100 text-red-800 px-4 py-2 rounded-lg text-sm flex items-center">
                                    <ShieldAlert size={16} className="mr-2 flex-shrink-0" /> This will create a new tracking ID in the Issue Management module.
                                </div>
                                <div className="grid grid-cols-3 gap-x-4 gap-y-3">
                                    <FormControl.SearchSelect
                                        label="Admin Risk Severity"
                                        options={SEVERITY_OPTIONS}
                                        value={riskSeverity}
                                        onChange={v => setRiskSeverity(String(v ?? 'Medium'))}
                                        data-testid="SEL_incident_triage_escalate_severity"
                                    />
                                    <FormControl.DatePicker
                                        label="Target Date"
                                        value={targetDate}
                                        onChange={e => { setTargetDate(e.target.value); clearError('targetDate'); }}
                                        onBlur={() => validateField('targetDate', 305, targetDate)}
                                        error={errors.targetDate}
                                        data-testid="INP_incident_triage_escalate_target_date"
                                    />
                                    <FormControl.SearchSelect
                                        label="Assign RCA Owner"
                                        options={ownerOptions}
                                        value={capaOwnerId}
                                        onChange={v => setCapaOwnerId(v == null ? null : Number(v))}
                                        placeholder="Select Owner..."
                                        clearable
                                        data-testid="SEL_incident_triage_escalate_owner"
                                    />
                                </div>
                                <FormControl.Textarea
                                    label="Instructions for Investigator"
                                    rows={4}
                                    placeholder="Specific instructions on what needs to be investigated..."
                                    value={instructions}
                                    onChange={e => { setInstructions(e.target.value); clearError('instructions'); }}
                                    onBlur={() => validateField('instructions', 110, instructions)}
                                    maxLength={maxLenForCode(110)}
                                    error={errors.instructions}
                                    data-testid="INP_incident_triage_escalate_instructions"
                                />
                            </div>
                        )}

                        {mode === 'task' && (
                            <div className="space-y-3">
                                <div className="bg-blue-50 border border-blue-100 text-blue-800 px-4 py-2 rounded-lg text-sm flex items-center">
                                    <ClipboardList size={16} className="mr-2 flex-shrink-0" /> Creates a real task linked to this incident. It will appear in the assignee&apos;s Action Centre.
                                </div>
                                <FormControl.Input
                                    label="Task Title"
                                    required
                                    value={taskTitle}
                                    onChange={e => { setTaskTitle(e.target.value); clearError('taskTitle'); }}
                                    onBlur={() => validateField('taskTitle', 109, taskTitle, { required: true })}
                                    maxLength={maxLenForCode(109)}
                                    error={errors.taskTitle}
                                    data-testid="INP_incident_triage_task_title"
                                />
                                <FormControl.Textarea
                                    label="Description (optional)"
                                    rows={3}
                                    placeholder="Extra context beyond the incident summary…"
                                    value={taskDescription}
                                    onChange={e => { setTaskDescription(e.target.value); clearError('taskDescription'); }}
                                    onBlur={() => validateField('taskDescription', 110, taskDescription)}
                                    maxLength={maxLenForCode(110)}
                                    error={errors.taskDescription}
                                    data-testid="INP_incident_triage_task_description"
                                />
                                <div className="grid grid-cols-3 gap-x-4 gap-y-3">
                                    <FormControl.SearchSelect
                                        label="Doer *"
                                        options={ownerOptions}
                                        value={taskAssigneeId}
                                        onChange={v => setTaskAssigneeId(v == null ? null : Number(v))}
                                        placeholder="Select…"
                                        clearable
                                        data-testid="SEL_incident_triage_task_doer"
                                    />
                                    <FormControl.SearchSelect
                                        label="Approver"
                                        options={ownerOptions}
                                        value={taskApproverId}
                                        onChange={v => setTaskApproverId(v == null ? null : Number(v))}
                                        placeholder="Optional"
                                        clearable
                                        data-testid="SEL_incident_triage_task_approver"
                                    />
                                    <FormControl.SearchSelect
                                        label="Validator"
                                        options={ownerOptions}
                                        value={taskValidatorId}
                                        onChange={v => setTaskValidatorId(v == null ? null : Number(v))}
                                        placeholder="Optional"
                                        clearable
                                        data-testid="SEL_incident_triage_task_validator"
                                    />
                                </div>
                                <div className="grid grid-cols-3 gap-x-4 gap-y-3">
                                    <FormControl.DatePicker
                                        label="Due Date"
                                        value={taskDueDate}
                                        onChange={e => { setTaskDueDate(e.target.value); clearError('taskDueDate'); }}
                                        onBlur={() => validateField('taskDueDate', 305, taskDueDate)}
                                        error={errors.taskDueDate}
                                        data-testid="INP_incident_triage_task_due_date"
                                    />
                                    <FormControl.Input
                                        label="Grace Period (days)"
                                        type="number"
                                        min={0}
                                        step={1}
                                        value={String(taskGraceDays)}
                                        onChange={e => { setTaskGraceDays(Math.max(0, Number(e.target.value || 0))); clearError('taskGraceDays'); }}
                                        onBlur={() => validateField('taskGraceDays', 206, String(taskGraceDays))}
                                        error={errors.taskGraceDays}
                                        data-testid="INP_incident_triage_task_grace_days"
                                    />
                                    <FormControl.SearchSelect
                                        label="Priority"
                                        options={[
                                            { value: 'LOW', label: 'Low' },
                                            { value: 'MEDIUM', label: 'Medium' },
                                            { value: 'HIGH', label: 'High' },
                                            { value: 'CRITICAL', label: 'Critical' },
                                        ]}
                                        value={taskPriority || null}
                                        onChange={v => setTaskPriority(v == null ? '' : String(v))}
                                        placeholder="Optional"
                                        clearable
                                        data-testid="SEL_incident_triage_task_priority"
                                    />
                                </div>
                            </div>
                        )}
                            </div>
                            </div>
                        </div>
                        )}
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer>
                {canTriage && activeSubject?.onCloseRecord && linkedTask && TASK_CLOSEABLE_STATUSES.has(linkedTask.status) && (
                    <Button
                        variant="fill"
                        icon={Lock}
                        type="button"
                        onClick={handleCloseRecord}
                        disabled={submitting}
                        data-testid="BTN_incident_triage_close_task"
                    >
                        {submitting ? 'Closing…' : `Close Incident (task is ${linkedTask.status})`}
                    </Button>
                )}
                {canTriage && activeSubject?.onCloseRecord && linkedIssue && linkedIssue.is_resolved && (
                    <Button
                        variant="fill"
                        icon={Lock}
                        type="button"
                        onClick={handleCloseRecord}
                        disabled={submitting}
                        data-testid="BTN_incident_triage_close_capa"
                    >
                        {submitting ? 'Closing…' : 'Close Incident (CAPA resolved)'}
                    </Button>
                )}
                {mode === 'trivial' && (
                    <Button variant="fill" type="button" onClick={handleSubmitTrivial} disabled={submitting} data-testid="BTN_incident_triage_submit_trivial">
                        {submitting ? 'Submitting...' : 'Confirm & Close'}
                    </Button>
                )}
                {mode === 'escalate' && (
                    <Button variant="fill" type="button" onClick={handleSubmitEscalation} disabled={submitting} data-testid="BTN_incident_triage_submit_escalate">
                        {submitting ? 'Submitting...' : 'Create CAPA Issue'}
                    </Button>
                )}
                {mode === 'task' && (
                    <Button variant="fill" type="button" onClick={handleSubmitTask} disabled={submitting} data-testid="BTN_incident_triage_submit_task">
                        {submitting ? 'Submitting...' : 'Create Task'}
                    </Button>
                )}
            </Modal.Footer>
            <Toast toast={toast} onClose={() => setToast(null)} />
        </Modal>
    );
};

export default TriageWorkbench;
